import { NotificationManager } from '../../utils/notifications.js';
import { MenuItemModal } from '../MenuItemModal.js';
import { AnimationManager } from '../../services/AnimationManager.js';
import { setupResponsiveGrid } from '../../utils/gridColumns.js';

export class CategoryGridLayout {
  constructor(config, menuData, cartCount = 0, i18n = null) {
    this.config = config;
    this.menuData = menuData;
    this.cartCount = cartCount;
    this.currentCategory = null;
    this.i18n = i18n;
    this.menuItemModal = new MenuItemModal(this.i18n);
    this.isCategoryViewOpen = false;
    this.animationManager = new AnimationManager();

    this.columnsPerRow = this.config?.kioskDisplaySettings?.layoutPreferences?.columnsPerRow || 3;

    this.featuredItems = this.getFeaturedItems();
    if (!this.menuData.items) {
      this.menuData.items = {};
    }
    if (this.featuredItems.length > 0) {
      this.menuData.items.featured = this.featuredItems;
    }
    this.categoriesList = this.buildCategories();
    if (!this.currentCategory) {
      this.currentCategory = this.categoriesList[0]?.id || this.menuData.categories?.[0]?.id || 'featured';
    }
  }

  render() {
    return `
      <div class="main-container grid-layout" style="display: none;">
        <div class="menu-container">
          <header>
            <div class="logo-container">
              ${this.renderLogoButton()}
            </div>
            <div class="header-actions">
              <button class="search-btn" id="grid-search-btn">
                <i class="fas fa-search"></i>
              </button>
              <button class="language-btn" id="language-btn">
                <i class="fas fa-globe"></i>
              </button>
              <button class="staff-call-btn" id="staff-call-btn">
                <i class="fas fa-bell"></i>
              </button>
              <div class="cart-btn" id="grid-cart-btn">
                <i class="fas fa-receipt"></i>
                <span class="cart-count">${this.cartCount}</span>
              </div>
            </div>
          </header>

          <div class="categories-section">
            ${this.renderCategoryGrid()}
          </div>
        </div>

        <!-- Category View Overlay -->
        <div class="category-view-overlay" id="category-view-overlay" style="display: none;">
          <div class="category-view-container">
            <button class="close-btn" id="category-close-btn">
              <i class="fas fa-times"></i>
            </button>
            <div class="category-view-content" id="category-view-content">
              <!-- Category items will be rendered here -->
            </div>
          </div>
        </div>
      </div>
    `;
  }

  renderCategoryGrid() {
    return `
      <div class="categories-grid" id="categories-grid">
        ${this.categoriesList.map(category => {
          const categoryImage = category.image || this.getFirstProductImage(category.id);
          const tileClasses = ['category-tile'];
          if (category.id === this.currentCategory) {
            tileClasses.push('active');
          }
          if (category.isFeaturedVirtual) {
            tileClasses.push('featured-category');
          }

          const imageMarkup = categoryImage ?
            `<img src="${categoryImage}" alt="${category.name}" class="category-image">` :
            `<div class="category-image-placeholder">
              <i class="fas ${category.icon || 'fa-star'}"></i>
            </div>`;

          return `
            <div class="${tileClasses.join(' ')}" data-category="${category.id}">
              ${imageMarkup}
              <div class="category-overlay">
                <div class="category-content">
                  <h4 class="category-name">${category.name}</h4>
                </div>
              </div>
            </div>
          `;
        }).join('')}
      </div>
    `;
  }

  renderLogoButton() {
    if (!this.config.branding?.logo) {
      return '';
    }
    return `
      <button class="logo-small-button" id="grid-logo-btn" aria-label="Back to welcome screen">
        <img src="${this.config.branding.logo}" alt="Logo" class="logo-small">
      </button>
    `;
  }


  getDietaryIcon(dietary) {
    const icons = {
      vegetarian: 'fas fa-leaf',
      vegan: 'fas fa-seedling',
      'gluten-free': 'fas fa-wheat',
      spicy: 'fas fa-pepper-hot'
    };
    return icons[dietary] || 'fas fa-circle';
  }

  bindEvents() {
    const categoriesGrid = document.getElementById('categories-grid');
    if (categoriesGrid) {
      setupResponsiveGrid(categoriesGrid, this.columnsPerRow);
    }

    const categoryTiles = document.querySelectorAll('.grid-layout .category-tile');
    categoryTiles.forEach(tile => {
      tile.addEventListener('click', () => {
        const categoryId = tile.getAttribute('data-category');
        this.selectCategory(categoryId);
      });
    });

    const searchBtn = document.getElementById('grid-search-btn');
    const cartBtn = document.getElementById('grid-cart-btn');
    const logoBtn = document.getElementById('grid-logo-btn');

    if (searchBtn) {
      searchBtn.addEventListener('click', () => {
        NotificationManager.info('Search functionality coming soon!');
      });
    }

    if (cartBtn) {
      cartBtn.addEventListener('click', () => {
        const event = new CustomEvent('showOrderConfirmation');
        document.dispatchEvent(event);
      });
    }

    const languageBtn = document.getElementById('language-btn');
    if (languageBtn) {
      languageBtn.addEventListener('click', () => {
        const event = new CustomEvent('showLanguageSelector');
        document.dispatchEvent(event);
      });
    }

    const staffCallBtn = document.getElementById('staff-call-btn');
    if (staffCallBtn) {
      staffCallBtn.addEventListener('click', () => {
        const event = new CustomEvent('showStaffCallModal');
        document.dispatchEvent(event);
      });
    }

    if (logoBtn) {
      logoBtn.addEventListener('click', () => {
        const event = new CustomEvent('navigateToWelcome');
        document.dispatchEvent(event);
      });
    }

    const menuItemCards = document.querySelectorAll('.grid-layout .menu-item-card');
    menuItemCards.forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('.add-to-cart') || e.target.closest('.image-nav-btn')) {
          return;
        }

        const itemId = card.getAttribute('data-item');
        const item = this.findItemById(itemId);
        if (item) {
          const allItems = this.getAllItems();
          const availableAllItems = allItems.filter(item => item.isAvailable !== false);
          this.menuItemModal.show(item, availableAllItems);
        }
      });
    });

    const addToCartBtns = document.querySelectorAll('.grid-layout .add-to-cart');
    addToCartBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const itemId = btn.getAttribute('data-item');
        this.handleAddToCart(itemId, btn);
      });
    });

    this.bindImageNavigation();

    const closeBtn = document.getElementById('category-close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.closeCategoryView();
      });
    }

    const overlay = document.getElementById('category-view-overlay');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.closeCategoryView();
        }
      });
    }
  }

  async selectCategory(categoryId) {
    this.currentCategory = categoryId;
    this.isCategoryViewOpen = true;

    const items = this.menuData.items[categoryId] || [];

    const selectedTile = document.querySelector(`.grid-layout .category-tile[data-category="${categoryId}"]`);
    if (selectedTile) {
      this.animationManager.animateCategorySelection(selectedTile);
    }

    const overlay = document.getElementById('category-view-overlay');
    if (overlay) {
      const container = overlay.querySelector('.category-view-container');
      if (container) {
        container.scrollTop = 0;
      }
      overlay.style.display = 'flex';
      overlay.style.opacity = '0';
      overlay.style.transform = 'scale(0.95)';

      const contentEl = document.getElementById('category-view-content');
      if (contentEl) {
        const menuGrid = document.createElement('div');
        menuGrid.className = 'menu-items-grid';
        menuGrid.style.opacity = '0';
        menuGrid.style.transition = 'opacity 0.3s ease-in-out';
        contentEl.innerHTML = '';
        contentEl.appendChild(menuGrid);

        setupResponsiveGrid(menuGrid, this.columnsPerRow);

        menuGrid.innerHTML = this.renderCategoryItems(categoryId);

        requestAnimationFrame(() => {
          overlay.style.transition = 'all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)';
          overlay.style.opacity = '1';
          overlay.style.transform = 'scale(1)';
        });

        setTimeout(() => {
          menuGrid.style.opacity = '1';
          this.animationManager.animateMenuItems(menuGrid, 30);

          this.bindCategoryViewEvents();
        }, 50);
      }
    }
  }

  bindCategoryViewEvents() {
    const overlay = document.getElementById('category-view-overlay');
    if (!overlay) return;

    const itemCards = overlay.querySelectorAll('.menu-item-card');
    itemCards.forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('.add-to-cart') || e.target.closest('.image-nav-btn')) {
          return;
        }
        const itemId = card.getAttribute('data-item');
        const item = this.findItemById(itemId);
        if (item) {
          const categoryItems = this.menuData.items[this.currentCategory] || [];
          const availableCategoryItems = categoryItems.filter(item => item.isAvailable !== false);
          this.menuItemModal.show(item, availableCategoryItems);
        }
      });
    });

    const addToCartBtns = overlay.querySelectorAll('.add-to-cart');
    addToCartBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const itemId = btn.getAttribute('data-item');
        this.handleAddToCart(itemId, btn);
      });
    });

    const imageNavBtns = overlay.querySelectorAll('.image-nav-btn');
    imageNavBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const direction = btn.getAttribute('data-direction');
        const card = btn.closest('.menu-item-card');
        this.switchImage(card, direction);
      });
    });
  }

  bindImageNavigation() {
    const imageNavBtns = document.querySelectorAll('.grid-layout .image-nav-btn');
    imageNavBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const direction = btn.getAttribute('data-direction');
        const card = btn.closest('.menu-item-card');
        this.switchImage(card, direction);
      });
    });
  }

  switchImage(card, direction) {
    const container = card.querySelector('.item-image-container');
    const img = container.querySelector('.item-image');
    const indicator = card.querySelector('.image-indicator');

    if (!img || !img.dataset.images) return;

    const images = JSON.parse(img.dataset.images);
    const currentIndex = parseInt(container.dataset.currentImage || '0');

    let newIndex;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % images.length;
    } else {
      newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    }

    img.src = images[newIndex];
    container.dataset.currentImage = newIndex;

    if (indicator) {
      indicator.textContent = `${newIndex + 1} of ${images.length}`;
    }
  }

  renderCategoryItems(categoryId) {
    const items = this.menuData.items[categoryId] || [];
    const availableItems = items.filter(item => item.isAvailable !== false);
    const isInFeaturedCategory = categoryId === 'featured';

    return availableItems.map(item => {
      const images = item.images || (item.image ? [item.image] : []);
      const cardClasses = ['menu-item-card'];
      if (this.isFeatured(item) && !isInFeaturedCategory) {
        cardClasses.push('is-featured');
      }

      return `
      <div class="${cardClasses.join(' ')}" data-item="${item.id}">
        <div class="item-image-container">
          ${images.length > 0 ?
            `<img src="${images[0]}" alt="${item.name}" class="item-image">` :
            `<div class="item-image-placeholder">
              <i class="fas fa-utensils"></i>
            </div>`}

          ${this.renderBadge(item)}
          ${item.dietary && item.dietary.length > 0 ? `
            <div class="dietary-icons">
              ${item.dietary.map(diet => `
                <div class="dietary-icon ${diet}">
                  <i class="${this.getDietaryIcon(diet)}"></i>
                </div>
              `).join('')}
            </div>
          ` : ''}
        </div>
        <div class="item-content">
          <h3 class="item-name">${item.name}</h3>
          <p class="item-description">${item.description}</p>
          <div class="item-footer">
            <div class="item-price">${item.currency || this.config.settings?.currency || 'PLN'} ${typeof item.price === 'object' ? item.price.amount : (item.price || 0)}</div>
            <button class="add-to-cart" data-item="${item.id}">
              <i class="fas fa-plus"></i>
            </button>
          </div>
        </div>
      </div>
    `;
    }).join('');
  }

  t(key, fallback) {
    if (!this.i18n || typeof this.i18n.t !== 'function') {
      return fallback;
    }
    const translation = this.i18n.t(key);
    return translation === key ? fallback : translation;
  }

  getFeaturedLabel() {
    return this.t('menu.featured', 'Featured');
  }

  buildCategories() {
    const base = Array.isArray(this.menuData.categories)
      ? this.menuData.categories.filter(cat => {
          const id = (cat.id || '').toString().toLowerCase();
          const name = (cat.name || '').toString().toLowerCase();
          return id !== 'featured' &&
                 id !== 'wyróżnione' &&
                 name !== 'featured' &&
                 name !== 'wyróżnione';
        })
      : [];

    if (this.featuredItems.length > 0) {
      const label = this.getFeaturedLabel();
      base.unshift({
        id: 'featured',
        name: label,
        icon: 'fa-star',
        image: null,
        isFeaturedVirtual: true
      });
    }
    return base;
  }

  getFeaturedItems() {
    const collected = new Map();

    if (Array.isArray(this.menuData?.specials)) {
      this.menuData.specials.forEach(item => {
        if (item && item.id) {
          collected.set(item.id, item);
        }
      });
    }

    if (this.menuData?.items) {
      Object.values(this.menuData.items).forEach(list => {
        (list || []).forEach(item => {
          if (item && item.id && this.isFeatured(item)) {
            collected.set(item.id, item);
          }
        });
      });
    }

    return Array.from(collected.values()).sort((a, b) => {
      const orderA = a.displayOrder ?? 0;
      const orderB = b.displayOrder ?? 0;
      if (orderA === orderB) {
        return a.name.localeCompare(b.name);
      }
      return orderA - orderB;
    });
  }

  isFeatured(item) {
    if (!item) return false;
    const badge = (item.badge || '').toString().toLowerCase();
    if (badge === 'featured' || badge === 'recommended') {
      return true;
    }
    const status = (item.status || '').toString().toLowerCase();
    if (status === 'recommended') {
      return true;
    }
    if (item.isPromotedToday || item.metadata?.isPromotedToday) {
      return true;
    }
    return false;
  }

  renderBadge(item) {
    if (!item) return '';
    const badgeKey = (item.badge || '').toString().toLowerCase();
    const label = item.badgeText || this.t('menu.badge.recommended', 'Recommended');

    if (this.isFeatured(item)) {
      return `
        <div class="badge-corner" aria-label="${label}">
          <span class="badge-star" aria-hidden="true">★</span>
          <span class="badge-label">${label}</span>
        </div>
      `;
    }

    if (badgeKey) {
      return `<span class="badge ${item.badge}">${item.badgeText || item.badge}</span>`;
    }
    return '';
  }

  closeCategoryView() {
    this.isCategoryViewOpen = false;
    const overlay = document.getElementById('category-view-overlay');
    if (overlay) {
      overlay.style.transition = 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)';
      overlay.style.opacity = '0';
      overlay.style.transform = 'scale(0.95)';

      setTimeout(() => {
        overlay.style.display = 'none';
        overlay.style.transition = '';
        overlay.style.transform = '';
      }, 300);
    }
  }

  handleAddToCart(itemId, buttonElement) {
    const item = this.findItemById(itemId);
    if (!item) {
      console.error('Item not found:', itemId);
      return;
    }

    const cartItem = {
      id: item.id,
      name: item.name,
      price: item.price?.amount || item.price || 0,
      quantity: 1,
      image: item.image || item.imageUrl,
      currency: item.currency || this.config.settings?.currency || this.config.currency || 'PLN',
      modifiers: {},
      specialInstructions: '',
      totalPrice: item.price?.amount || item.price || 0
    };

    const event = new CustomEvent('itemAddedToCart', {
      detail: cartItem
    });
    document.dispatchEvent(event);

    this.animateAddToCartButton(buttonElement);
  }

  /**
   * Animate add-to-cart button with satisfying feedback
   */
  animateAddToCartButton(buttonElement) {
    buttonElement.innerHTML = '<i class="fas fa-check"></i>';
    buttonElement.style.background = 'var(--color-success, #3E885B)';
    buttonElement.style.color = 'white';
    buttonElement.style.transform = 'scale(1.2)';
    buttonElement.style.transition = 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)';

    setTimeout(() => {
      buttonElement.style.transform = 'scale(1.1)';
    }, 150);

    setTimeout(() => {
      buttonElement.innerHTML = '<i class="fas fa-plus"></i>';
      buttonElement.style.background = '';
      buttonElement.style.color = '';
      buttonElement.style.transform = '';
      buttonElement.style.transition = 'all 0.3s ease';
    }, 800);
  }

  findItemById(itemId) {
    for (const categoryItems of Object.values(this.menuData.items)) {
      const item = categoryItems.find(item => item.id === itemId);
      if (item) return item;
    }
    return null;
  }

  getFirstProductImage(categoryId) {
    if (categoryId === 'featured') {
      return this.featuredItems[0]?.image || this.featuredItems[0]?.images?.[0] || null;
    }
    const items = this.menuData.items[categoryId] || [];
    const itemWithImage = items.find(item => item.image);
    return itemWithImage ? itemWithImage.image : null;
  }

  show() {
    const container = document.querySelector('.main-container.grid-layout');
    if (container) {
      container.style.display = 'block';
      container.style.opacity = '0';

      requestAnimationFrame(() => {
        container.style.transition = 'opacity 0.4s ease';
        container.style.opacity = '1';

        setTimeout(() => {
          this.loadInitialCategoryGridWithAnimations();
        }, 500);
      });
    }
  }

  loadInitialCategoryGridWithAnimations() {
    const categoriesGrid = document.querySelector('.grid-layout .categories-grid');
    if (categoriesGrid) {
      const categoryTiles = categoriesGrid.querySelectorAll('.category-tile');
      categoryTiles.forEach((tile, index) => {
        tile.style.opacity = '0';
        tile.style.transform = 'translateY(30px) scale(0.9)';
        tile.style.transition = 'all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)';

        setTimeout(() => {
          tile.style.opacity = '1';
          tile.style.transform = 'translateY(0) scale(1)';
        }, index * 60 + 150);
      });
    }
  }

  hide() {
    return new Promise((resolve) => {
      const container = document.querySelector('.main-container.grid-layout');
      if (container) {
        container.style.display = 'none';
      }
      resolve();
    });
  }

  updateCartCount(count) {
    this.cartCount = count;
    const cartCountEl = document.querySelector('.grid-layout .cart-count');
    if (cartCountEl) {
      cartCountEl.textContent = count;
    }
  }

  getAllItems() {
    const allItems = [];
    for (const categoryItems of Object.values(this.menuData.items)) {
      allItems.push(...categoryItems);
    }
    return allItems;
  }

  getSupportedFeatures() {
    return ['search', 'cart', 'categoryGrid', 'categories'];
  }
}
